import {
  Box,
  LinearProgress,
  MenuItem,
  Modal,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import React from "react";
import { FilledButton } from "../../../../../customComponents/styled/styledButtons";
import MathContent from "../../../../../customComponents/MathContent";
import recentShared from "../../../../../../assets/icons/recent-shared.svg";
import { Delete, CheckCircle } from "@mui/icons-material";
import { useDeleteQuizMutation, useGetQuizQuery } from "../../../../../../app/quizApiSlice";
import CustomClickableInfoCard from "../../../../../customComponents/CustomClickableInfoCard";

const SharedQuizzes = () => {
  const [sortBy, setSortBy] = React.useState("subject");
  const [page, setPage] = React.useState(1);
  const itemsPerPage = 10;
  const { data, isLoading } = useGetQuizQuery();
  const [deleteQuiz, { isLoading: isDeletingQuiz }] = useDeleteQuizMutation();

  const [openModals, setOpenModals] = React.useState({});
  const [deletedModals, setDeletedModals] = React.useState({});
  const [previewModals, setPreviewModals] = React.useState({});

  const handleOpenModal = (quizId) => {
    setOpenModals((prev) => ({ ...prev, [quizId]: true }));
  };

  const handleCloseModal = (quizId) => {
    setOpenModals((prev) => ({ ...prev, [quizId]: false }));
  };

  const handleDelete = async (quizId) => {
    await deleteQuiz({ id: quizId }).unwrap();
    handleCloseModal(quizId);
    setDeletedModals((prev) => ({ ...prev, [quizId]: true }));
  };

  const handleCloseDeletedModal = (quizId) => {
    setDeletedModals((prev) => ({ ...prev, [quizId]: false }));
  };

  const handleOpenPreviewModal = (quiz) => {
    setPreviewModals((prev) => ({ ...prev, [quiz.id]: true }));
  };

  const handleClosePreviewModal = (quizId) => {
    setPreviewModals((prev) => ({ ...prev, [quizId]: false }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      throw new Error("Invalid date string");
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const sortedQuizzes = React.useMemo(() => {
    if (!data?.quizzes) return [];
    return [...data.quizzes].sort((a, b) => {
      if (sortBy === "level") {
        return a.level.localeCompare(b.level);
      } else if (sortBy === "subject") {
        return a.subject.localeCompare(b.subject);
      } else if (sortBy === "date") {
        return (
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
      }
      return 0;
    });
  }, [data?.quizzes, sortBy]);

  const paginatedQuizzes = React.useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return sortedQuizzes.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedQuizzes, page]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeSort = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const totalQuizzes = sortedQuizzes.length;
  const startIndex = (page - 1) * itemsPerPage + 1;
  const endIndex = Math.min(page * itemsPerPage, totalQuizzes);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          marginBottom: "1em",
        }}
      >
        <Select value={sortBy} onChange={handleChangeSort} size="small">
          <MenuItem value="subject">
            Sort by: <strong>Subject</strong>
          </MenuItem>
          <MenuItem value="level">
            Sort by: <strong>Level</strong>
          </MenuItem>
          <MenuItem value="date">
            Sort by: <strong>Date</strong>
          </MenuItem>
        </Select>
      </Box>

      {isLoading && <LinearProgress />}

      {paginatedQuizzes.map((quiz) => (
        <Box sx={{ width: "100%" }} key={quiz.id}>
          <CustomClickableInfoCard
            icon={recentShared}
            title={quiz.subject}
            details={quiz.questions[0]?.topic}
            date={formatDate(quiz.updated_at)}
            action={"Delete"}
            onCardClick={handleOpenPreviewModal}
            logo={<Delete />}
            handleClick={() => handleOpenModal(quiz.id)}
            cardData={quiz}
          />
          <Modal
            open={openModals[quiz.id] || false}
            onClose={() => handleCloseModal(quiz.id)}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
                padding: "2.5em",
                borderRadius: "24px",
                width: { xs: "90%", sm: "70%", md: "55vw" },
                maxWidth: { xs: "75vw", md: "55vw" },
              }}
            >
              <Box
                sx={{
                  backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  padding: "1em 0",
                  borderRadius: "21px",
                }}
              >
                <Box sx={{ width: "2.6em", minWidth: "2.4em" }}>
                  <img src={recentShared} alt="" style={{ width: "100%" }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: ".9rem", md: "1.25rem" },
                    fontWeight: 700,
                    color: "#1E1E1E",
                    margin: ".3em 0",
                  }}
                >
                  {quiz.questions[0]?.topic}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: ".75rem", md: ".9rem" },
                    fontWeight: 400,
                    color: "text.secondary",
                  }}
                >
                  {formatDate(quiz.created_at)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  marginTop: "1.5em",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: "1rem", md: "1.25rem" },
                    fontWeight: 700,
                    color: "#1E1E1E",
                  }}
                >
                  Confirmation
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: ".75rem", md: ".9rem" },
                    fontWeight: 400,
                    color: "#95969D",
                    marginTop: "1em",
                    marginBottom: "1.5em",
                    textAlign: "center",
                  }}
                >
                  Are you sure you want to stop sharing this quiz?
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <FilledButton
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: ".8rem",
                      letterSpacing: "1px",
                      width: { xs: "10em", md: "17em" },
                    }}
                    onClick={() => handleDelete(quiz.id)}
                    disabled={isDeletingQuiz}
                  >
                    Delete
                  </FilledButton>
                  <FilledButton
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: ".8rem",
                      letterSpacing: "1px",
                      width: { xs: "10em", md: "17em" },
                    }}
                    onClick={() => handleCloseModal(quiz.id)}
                    disabled={isDeletingQuiz}
                  >
                    Cancel
                  </FilledButton>
                </Box>
              </Box>
            </Box>
          </Modal>
          <Modal
            open={deletedModals[quiz.id] || false}
            onClose={() => handleCloseDeletedModal(quiz.id)}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
                padding: "2.5em",
                borderRadius: "24px",
                width: { xs: "90%", md: "55vw" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <CheckCircle
                  sx={{
                    width: "7.5em",
                    height: "7.5em",
                    color: "primary.main",
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    color: "#1E1E1E",
                    marginTop: "1em",
                  }}
                >
                  Success
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: ".9rem",
                    fontWeight: 400,
                    color: "#95969D",
                    marginTop: ".8em",
                    textAlign: "center",
                  }}
                >
                  Your quiz has been deleted successfully!
                </Typography>
              </Box>
            </Box>
          </Modal>
          <Modal
            open={previewModals[quiz.id] || false}
            onClose={() => handleClosePreviewModal(quiz.id)}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
                padding: "2.5em",
                borderRadius: "24px",
                width: { xs: "90%", sm: "85%", md: "70vw" },
                maxWidth: "90vw",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.5em",
                  borderBottom: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#333" : "#E0E0E0"}`,
                  paddingBottom: "1em",
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.35rem" },
                      fontWeight: 700,
                      color: "#1E1E1E",
                    }}
                  >
                    Quiz Preview
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: ".9rem",
                      fontWeight: 400,
                      color: "#95969D",
                      marginTop: ".3em",
                    }}
                  >
                    {quiz.subject} • {quiz.level} • {quiz.questions.length} questions
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {quiz.questions.map((question, index) => (
                  <Box
                    key={question.id}
                    sx={{
                      padding: "1.2em",
                      backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                      borderRadius: "8px",
                      border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E8E8E8"}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: ".95rem",
                        fontWeight: 600,
                        color: "#1E1E1E",
                        marginBottom: ".8em",
                      }}
                    >
                      <strong>{index + 1}.</strong>
                    </Typography>
                    <Box
                      sx={{
                        fontSize: ".95rem",
                        color: "#1E1E1E",
                        marginBottom: ".8em",
                        "& p": { margin: 0, marginBottom: "0.5em" },
                        "& h1, & h2, & h3, & h4, & h5, & h6": { margin: 0, marginBottom: "0.5em", fontSize: "1em" },
                        "& ul, & ol": { marginLeft: "1.5em", marginBottom: "0.5em" },
                        "& li": { marginBottom: "0.3em" },
                        "& strong": { fontWeight: 600 },
                        "& em": { fontStyle: "italic" },
                        "& code": { backgroundColor: "#f5f5f5", padding: "0.2em 0.4em", borderRadius: "3px", fontFamily: "monospace", fontSize: "0.9em" },
                        "& pre": { backgroundColor: "#f5f5f5", padding: "0.5em", borderRadius: "4px", overflow: "auto", fontSize: "0.85em" },
                        "& blockquote": { borderLeft: "3px solid #ddd", paddingLeft: "0.8em", marginLeft: 0, color: "#666", fontStyle: "italic" },
                      }}
                    >
                      <MathContent html={question.body} />
                    </Box>

                    {question.options && question.options.length > 0 && (
                      <Box sx={{ ml: 2, display: "flex", flexDirection: "column", gap: 0.8 }}>
                        {question.options.map((option, optIndex) => (
                          <Box
                            key={option.id || optIndex}
                            sx={{
                              fontSize: ".85rem",
                              color: "#555555",
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 0.5,
                            }}
                          >
                            <span style={{ marginRight: "0.5em", fontWeight: 500, minWidth: "1.5em" }}>
                              {String.fromCharCode(65 + optIndex)}.
                            </span>
                            <Box
                              sx={{
                                flex: 1,
                                "& p": { margin: 0 },
                                "& strong": { fontWeight: 600 },
                                "& em": { fontStyle: "italic" },
                                "& code": { backgroundColor: "#f5f5f5", padding: "0.2em 0.4em", borderRadius: "3px", fontFamily: "monospace" },
                              }}
                            >
                              <MathContent html={option.body} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {question.topic && (
                      <Typography
                        sx={{
                          fontSize: ".8rem",
                          color: "text.secondary",
                          marginTop: ".8em",
                          fontStyle: "italic",
                        }}
                      >
                        Topic: {question.topic}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  marginTop: "1.5em",
                  borderTop: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#333" : "#E0E0E0"}`,
                  paddingTop: "1em",
                }}
              >
                <FilledButton
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    fontSize: ".8rem",
                    letterSpacing: "1px",
                    width: { xs: "100%", md: "12em" },
                  }}
                  onClick={() => handleClosePreviewModal(quiz.id)}
                >
                  Close
                </FilledButton>
              </Box>
            </Box>
          </Modal>
        </Box>
      ))}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "2em",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {startIndex} to {endIndex} of {totalQuizzes} entries
        </Typography>

        <Pagination
          count={Math.ceil(sortedQuizzes.length / itemsPerPage)}
          page={page}
          onChange={handleChangePage}
          color="primary"
          shape="rounded"
          sx={{ color: "#CCCCCC" }}
        />
      </Box>
    </Box>
  );
};

export default SharedQuizzes;
